import React, { useState, useMemo, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    FormControlLabel,
    Paper,
    Divider,
    Tooltip,
    IconButton
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import LoadingSwitch from "../../components/LoadingSwitch";
import MainCard from "../../components/cards/MainCard";
import {
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
} from "../../store/services/tenancy.service";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 900,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
};

AssignPackModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    contentPacks: PropTypes.array,
    groups: PropTypes.array,
    onConfirm: PropTypes.func,
    isLoading: PropTypes.bool,
    initialValues: PropTypes.object
};

export default function AssignPackModal({
    open,
    onClose,
    contentPacks = [],
    groups = [],
    onConfirm,
    isLoading = false,
    initialValues = null
}) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId } = useSelector((state) => state.tenancy);

    const [selectedContentPack, setSelectedContentPack] = useState('');
    const [selectedGrouping, setSelectedGrouping] = useState('');
    const [annualRefresh, setAnnualRefresh] = useState(false);
    const [showConfirmStep, setShowConfirmStep] = useState(false);
    const [sendToExistingChecked, setSendToExistingChecked] = useState(false);

    useEffect(() => {
        if (open && initialValues) {
            if (initialValues.contentPackId) setSelectedContentPack(initialValues.contentPackId);
            if (initialValues.grouping) setSelectedGrouping(initialValues.grouping);
            if (initialValues.annualRefresh !== undefined) setAnnualRefresh(initialValues.annualRefresh);
        }
    }, [open, initialValues]);

    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');
    const [availableItems, setAvailableItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    // Lazy queries for different grouping types
    const [triggerLocations, { data: locationsData, isLoading: locationsLoading }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData, isLoading: departmentsLoading }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerCities, { data: citiesData, isLoading: citiesLoading }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerCountries, { data: countriesData, isLoading: countriesLoading }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData, isLoading: companyNamesLoading }] = useLazyGetCompanyNamesByTenancyIdQuery();
    const [triggerStates, { data: statesData, isLoading: statesLoading }] = useLazyGetStatesByTenancyIdQuery();

    const groupingOptions = [
        { value: 'Tenancy', label: 'Tenancy' },
        { value: 'Group', label: 'Group' },
        { value: 'Location', label: 'Location' },
        { value: 'Department', label: 'Department' },
        { value: 'City', label: 'City' },
        { value: 'Country', label: 'Country' },
        { value: 'CompanyName', label: 'Company' },
        { value: 'State', label: 'State' },
    ];

    const handleGroupingChange = (event) => {
        const grouping = event.target.value;
        setSelectedGrouping(grouping);
        setSelectedItems([]);
        setAvailableItems([]);

        // Trigger the appropriate query
        switch (grouping) {
            case 'Tenancy':
                break;
            case 'Group':
                // Groups come from props, no query needed
                break;
            case 'Location':
                triggerLocations({ tenancyId });
                break;
            case 'Department':
                triggerDepartments({ tenancyId });
                break;
            case 'City':
                triggerCities({ tenancyId });
                break;
            case 'Country':
                triggerCountries({ tenancyId });
                break;
            case 'CompanyName':
                triggerCompanyNames({ tenancyId });
                break;
            case 'State':
                triggerStates({ tenancyId });
                break;
            default:
                break;
        }
    };

    // Get the current grouping data
    // Groups from props need to be mapped to { id, name } format
    const groupItems = useMemo(() =>
        (groups || []).map(g => ({ id: g.userGroupId, name: g.name })),
    [groups]);

    const groupingData = useMemo(() => {
        switch (selectedGrouping) {
            case 'Group':
                return groupItems;
            case 'Location':
                return locationsData || [];
            case 'Department':
                return departmentsData || [];
            case 'City':
                return citiesData || [];
            case 'Country':
                return countriesData || [];
            case 'CompanyName':
                return companyNamesData || [];
            case 'State':
                return statesData || [];
            default:
                return [];
        }
    }, [selectedGrouping, groupItems, locationsData, departmentsData, citiesData, countriesData, companyNamesData, statesData]);

    const isGroupingLoading = locationsLoading || departmentsLoading || citiesLoading || countriesLoading || companyNamesLoading || statesLoading;

    // Filter out already selected items from available
    const availableItemsFiltered = useMemo(() => {
        const selectedIds = selectedItems.map(item => item.id);
        return groupingData.filter(item => !selectedIds.includes(item.id));
    }, [groupingData, selectedItems]);

    const handleAddItem = (item) => {
        setSelectedItems([item]);
    };

    const handleRemoveItem = (item) => {
        setSelectedItems(prev => prev.filter(i => i.id !== item.id));
    };

    const handleProceedToConfirm = () => {
        setShowConfirmStep(true);
    };

    const handleFinalConfirm = (sendToExistingUsers) => {
        if (onConfirm) {
            onConfirm({
                contentPackId: selectedContentPack,
                grouping: selectedGrouping,
                annualRefresh,
                sendToExistingUsers,
                recipient: selectedItems[0] || null
            });
        }
        handleClose();
    };

    const handleBackToAssign = () => {
        setShowConfirmStep(false);
        setSendToExistingChecked(false);
    };

    const handleClose = () => {
        setSelectedContentPack('');
        setSelectedGrouping('');
        setAnnualRefresh(false);
        setShowConfirmStep(false);
        setSendToExistingChecked(false);
        setLeftSearch('');
        setRightSearch('');
        setSelectedItems([]);
        setAvailableItems([]);
        onClose();
    };

    const filteredAvailableItems = availableItemsFiltered.filter(item =>
        item.name.toLowerCase().includes(leftSearch.toLowerCase())
    );

    const filteredSelectedItems = selectedItems.filter(item =>
        item.name.toLowerCase().includes(rightSearch.toLowerCase())
    );

    return (
        <>
        <Modal
            open={open && !showConfirmStep}
            onClose={handleClose}
            aria-labelledby="assign-pack-modal"
        >
            <Box sx={modalStyle}>
                {/* Top Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                    {/* Left side - Dropdowns */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Content pack</InputLabel>
                            <Select
                                value={selectedContentPack}
                                onChange={(e) => setSelectedContentPack(e.target.value)}
                                label="Content pack"
                                disabled={!!initialValues?.contentPackId}
                            >
                                {contentPacks.map((pack) => (
                                    <MenuItem key={pack.internalId} value={pack.internalId}>
                                        {pack.contentPackName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>Select grouping</InputLabel>
                            <Select
                                value={selectedGrouping}
                                onChange={handleGroupingChange}
                                label="Select grouping"
                                disabled={!!initialValues?.grouping}
                            >
                                {groupingOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Right side - Annual Refresh & Confirm */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleProceedToConfirm}
                            disabled={!selectedContentPack || (selectedGrouping !== 'Tenancy' && selectedItems.length === 0)}
                        >
                            Confirm
                        </Button>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="When enabled, the content pack will be automatically re-sent to users on a yearly basis">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="body2">Annual Refresh</Typography>
                            <LoadingSwitch
                                checked={annualRefresh}
                                onChange={() => setAnnualRefresh(prev => !prev)}
                                loading={false}
                                inputProps={{ "aria-label": "annual refresh toggle" }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Bottom Section - Transfer Lists */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    ...(selectedGrouping === 'Tenancy' && {
                        opacity: 0.4,
                        pointerEvents: 'none',
                    })
                }}>
                    {/* Left Panel - Available Items */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Box sx={{ mb: 1, height: '24px' }} /> {/* Spacer to align with right panel label */}
                        <MainCard
                            content={false}
                            boxShadow
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: getCardColor(lightMode),
                                overflow: 'hidden'
                            }}
                        >
                        <Box sx={{ p: 1.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={leftSearch}
                                onChange={(e) => setLeftSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                            {isGroupingLoading ? (
                                <ListItem>
                                    <ListItemText primary="Loading..." />
                                </ListItem>
                            ) : filteredAvailableItems.length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary={selectedGrouping ? "No items available" : "Select a grouping"}
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </ListItem>
                            ) : (
                                filteredAvailableItems.map((item) => (
                                    <ListItem
                                        key={item.id}
                                        secondaryAction={
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleAddItem(item)}
                                                sx={{
                                                    minWidth: 32,
                                                    width: 32,
                                                    height: 32,
                                                    p: 0
                                                }}
                                            >
                                                <AddIcon fontSize="small" />
                                            </Button>
                                        }
                                        sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                                    >
                                        <ListItemText primary={item.name} />
                                    </ListItem>
                                ))
                            )}
                        </List>
                        </MainCard>
                    </Box>

                    {/* Right Panel - Selected Items */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            To send to
                        </Typography>
                        <MainCard
                            content={false}
                            boxShadow
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: getCardColor(lightMode),
                                overflow: 'hidden'
                            }}
                        >
                        <Box sx={{ p: 1.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={rightSearch}
                                onChange={(e) => setRightSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                            {filteredSelectedItems.length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary="No items selected"
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </ListItem>
                            ) : (
                                filteredSelectedItems.map((item) => (
                                    <ListItem
                                        key={item.id}
                                        secondaryAction={
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={() => handleRemoveItem(item)}
                                                sx={{
                                                    minWidth: 32,
                                                    width: 32,
                                                    height: 32,
                                                    p: 0
                                                }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </Button>
                                        }
                                        sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                                    >
                                        <ListItemText primary={item.name} />
                                    </ListItem>
                                ))
                            )}
                        </List>
                        </MainCard>
                    </Box>
                </Box>
            </Box>

        </Modal>

        {/* Confirmation Step Modal */}
        <Modal
            open={showConfirmStep}
            onClose={handleBackToAssign}
            aria-labelledby="confirm-send-modal"
        >
            <Paper sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 480,
                p: 3,
                borderRadius: 2,
                outline: 'none'
            }}>
                <Typography variant="h6" gutterBottom>
                    Create Assignment
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" sx={{ mb: 3 }}>
                    Would you like to send this content pack to all existing users in the selected scope now, or only to new users going forward?
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendToExistingChecked}
                            onChange={(e) => setSendToExistingChecked(e.target.checked)}
                        />
                    }
                    label="Send to all existing users in this scope"
                    sx={{ mb: 3, display: 'block' }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleBackToAssign}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleFinalConfirm(false)}
                    >
                        Create
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleFinalConfirm(true)}
                        disabled={!sendToExistingChecked}
                    >
                        Create & Send
                    </Button>
                </Box>
            </Paper>
        </Modal>
        </>
    );
}
